// ─── NR (Non-Rig) Workflow Models (M005) ─────────────────────────────────────

// ─── Conservation status ──────────────────────────────────────────────────────

/** Well conservation flags and date-window state for NR cases (BRU-006). */
export interface NrConservationStatus {
  gisSummary: boolean;
  timingZoneOne: boolean;
  timingZoneTwo: boolean;
  duneLizardNm: boolean;
  duneLizardTx: boolean;
  hornshellMussel: boolean;
  blmLand: boolean;
  withinConservationDates: boolean;
}

// ─── Status / Filter types ────────────────────────────────────────────────────

/** CaseStatus values derived from JobStatus (BRU-003, C-M5-01). */
export type NrCaseStatus =
  | 'Create Job'
  | 'In-Progress'
  | 'Planned'
  | 'Suspend Job'
  | 'Edit Job'
  | 'Resolved-Completed'
  | 'Resolved-Error';

export type NrJobStatus =
  | 'Create Job'
  | 'In-Progress'
  | 'Planned'
  | 'Suspend Job'
  | 'Complete'
  | 'Cancelled';

// ─── List / Page ─────────────────────────────────────────────────────────────

export interface NrCaseListQuery {
  page?: number;
  pageSize?: number;
  sort?: string;
  desc?: boolean;
}

export interface NrCasePage {
  items: NrCaseListItem[];
  totalCount: number;
}

export interface NrCaseListItem {
  id: number;
  caseId: number;
  caseNumber: string;
  wellName: string | null;
  api14: string;
  wellApiTen: string | null;
  jobStatus: string;
  caseStatus: string;
  liftMethod: string;
  fot: string;
  jobType: string | null;
  jobReason: string | null;
  startDate: string | null;
  endDate: string | null;
  statusChangeDate: string;
  statusChangeUser: string | null;
  createdDate: string;
  createdBy: string | null;
}

// ─── Detail ───────────────────────────────────────────────────────────────────

export interface NrCaseDetail {
  id: number;
  caseId: number;
  caseNumber: string;
  jobStatus: string;
  caseStatus: string;
  // Well Information
  wellName: string | null;
  api14: string;
  state: string;
  area: string;
  fot: string;
  liftMethod: string;
  costCenter: string;
  plantCode: string;
  companyCode: string;
  latitude: number;
  longitude: number;
  directionsToWell: string | null;
  // Job Details
  jobType: string | null;
  jobReason: string | null;
  jobDriver: string | null;
  startDate: string | null;
  endDate: string | null;
  afe: string | null;
  plannedCost: number | null;
  actualJobCost: number | null;
  remarks: string | null;
  // Metadata
  statusChangeDate: string;
  statusChangeUser: string | null;
  actualJobDurationDays: number | null;
  actualJobDurationHours: number | null;
  wellApiTen: string | null;
  createdDate: string;
  createdBy: string | null;
  // Conservation status from MasterWellList (BRU-006)
  conservationStatus: NrConservationStatus | null;
}

// ─── Request payloads ─────────────────────────────────────────────────────────

export interface CreateNrCaseRequest {
  caseId: number;
  wellName?: string | null;
  api14: string;
  state: string;
  area: string;
  fot: string;
  liftMethod: string;
  costCenter: string;
  plantCode: string;
  companyCode: string;
  latitude: number;
  longitude: number;
  directionsToWell?: string | null;
  jobType?: string | null;
  jobReason?: string | null;
  jobDriver?: string | null;
  jobStatus?: string;
  startDate?: string | null;
  endDate?: string | null;
  afe?: string | null;
  plannedCost?: number | null;
  actualJobCost?: number | null;
  remarks?: string | null;
}

export interface UpdateNrCaseRequest {
  wellName?: string | null;
  api14: string;
  state: string;
  area: string;
  fot: string;
  liftMethod: string;
  costCenter: string;
  plantCode: string;
  companyCode: string;
  latitude?: number | null;
  longitude?: number | null;
  directionsToWell?: string | null;
  jobType?: string | null;
  jobReason?: string | null;
  jobDriver?: string | null;
  jobStatus?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  afe?: string | null;
  plannedCost?: number | null;
  actualJobCost?: number | null;
  remarks?: string | null;
}

export interface NrCaseStatusRequest {
  jobStatus: string;
}

// ─── Attachment ───────────────────────────────────────────────────────────────

export interface NrAttachment {
  id: number;
  caseId: number;
  attachmentType: string;
  fileName: string;
  uploadedDate: string;
  uploadedBy: string | null;
  fileSizeBytes: number;
}

export interface NrAttachmentUrl {
  downloadUrl: string;
  fileName: string;
  urlExpiresAt: string;
}

// ─── Audit Log ────────────────────────────────────────────────────────────────

export interface NrAuditLogEntry {
  id: number;
  timestamp: string;
  actionType: string;
  actorName: string;
  details: string;
}

// ─── Conservation check ───────────────────────────────────────────────────────

export interface NrConservationCheckResult {
  isProtected: boolean;
  serviceUnavailable: boolean;
  protectedSpeciesMessage: string | null;
  guidanceItems: string[];
}
